import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Pencil, MessageSquare, X as XIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { PULP_OUTLINE_COLOR } from "./findingPalette.config";
import { getPillPosition, getSinglePenStyle, isSinglePenCode } from "./singlePenStyles";
import { isWholeToothSurfaceFillCode } from "./chartFindingBehavior";

// =====================================================================
// Tooth — single FDI tooth visual.
//
// Geometry copied from the Base 44 reference (audited 2026-04-28):
//   * 4 capsule-shaped surface "pills" arranged at top / right / bottom /
//     left — Buccal (B), Distal (D), Lingual (L), Mesial (M).
//   * 1 small circle in the centre — Occlusal (O).
//   * Quadrant-aware mapping: Mesial always points toward the midline,
//     Buccal toward the lip/cheek, Lingual toward the tongue.
//
// Locked v2 additions on top of the Base 44 baseline:
//   * Always-visible note pill BELOW each tooth (replaces Base 44's
//     hover-only pencil at top-right + amber dot).
//       - Empty:    gray pill outline + gray pencil ("add a note")
//       - Has note: filled orange pill + white pencil
//       - Read-only / past snapshot: still visible, opens a read-only viewer
//   * Optional "swap to primary/permanent" affordance for Mixed mode.
//
// Surface findings are strictly surface-specific. There is intentionally no
// double-click or long-press shortcut that fills all 5 surfaces; those gestures
// are too easy to trigger during fast chairside charting.
// =====================================================================

const VIEW   = 40;          // SVG viewBox edge
const CX = 20, CY = 20;
const OUTER_R = 17.5;       // outer edge of pills
const INNER_R = 9;          // inner edge of pills (gap from centre)
const CENTER_R = 7.5;       // occlusal centre circle
const GAP_HALF_ANGLE = 0.04;
const NOTE_PREVIEW_OPEN_DELAY_MS = 450;
const NOTE_PREVIEW_CLOSE_DELAY_MS = 160;
const COMPLETED_WORK_PREVIEW_OPEN_DELAY_MS = 450;
const COMPLETED_WORK_PREVIEW_CLOSE_DELAY_MS = 160;
const STACK_PREVIEW_OPEN_DELAY_MS = 450;
const STACK_PREVIEW_CLOSE_DELAY_MS = 160;
const COMPLETED_WORK_COLOR = "#14b8a6";
const STACK_FALLBACK_COLOR = "#94a3b8";

// Returns the SVG path d-string for one capsule "pill" centred at angle.
function capsulePath(centerAngle) {
  const capR = (OUTER_R - INNER_R) / 2;
  const midR = (OUTER_R + INNER_R) / 2;
  const capAng = capR / midR;
  const halfSpan = Math.PI / 4 - GAP_HALF_ANGLE - capAng;
  const a1 = centerAngle - halfSpan;
  const a2 = centerAngle + halfSpan;
  const ox1 = CX + OUTER_R * Math.cos(a1);
  const oy1 = CY + OUTER_R * Math.sin(a1);
  const ox2 = CX + OUTER_R * Math.cos(a2);
  const oy2 = CY + OUTER_R * Math.sin(a2);
  const ix1 = CX + INNER_R * Math.cos(a2);
  const iy1 = CY + INNER_R * Math.sin(a2);
  const ix2 = CX + INNER_R * Math.cos(a1);
  const iy2 = CY + INNER_R * Math.sin(a1);
  return [
    `M ${ox1} ${oy1}`,
    `A ${OUTER_R} ${OUTER_R} 0 0 1 ${ox2} ${oy2}`,
    `A ${capR} ${capR} 0 0 1 ${ix1} ${iy1}`,
    `A ${INNER_R} ${INNER_R} 0 0 0 ${ix2} ${iy2}`,
    `A ${capR} ${capR} 0 0 1 ${ox1} ${oy1}`,
    "Z",
  ].join(" ");
}

const PILL_PATHS = {
  top:    capsulePath(-Math.PI / 2),
  right:  capsulePath(0),
  bottom: capsulePath(Math.PI / 2),
  left:   capsulePath(Math.PI),
};

const SURFACE_STACK_BOUNDS = {
  top:    { x: 7, y: 2, width: 26, height: 11 },
  right:  { x: 28, y: 7, width: 10, height: 26 },
  bottom: { x: 7, y: 28, width: 26, height: 10 },
  left:   { x: 2, y: 7, width: 10, height: 26 },
  center: { x: CX - CENTER_R, y: CY - CENTER_R, width: CENTER_R * 2, height: CENTER_R * 2 },
};

// Label centre at the mid-radius of each pill.
const MID_R = (OUTER_R + INNER_R) / 2;
const LABEL_POS = {
  top:    { x: CX, y: CY - MID_R },
  right:  { x: CX + MID_R, y: CY },
  bottom: { x: CX, y: CY + MID_R },
  left:   { x: CX - MID_R, y: CY },
  center: { x: CX, y: CY },
};

function parseHexColor(hex) {
  const clean = String(hex || "").replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(clean)) return null;
  return {
    r: parseInt(clean.slice(0, 2), 16),
    g: parseInt(clean.slice(2, 4), 16),
    b: parseInt(clean.slice(4, 6), 16),
  };
}

function readableSurfaceLabelColor(hex) {
  const rgb = parseHexColor(hex);
  if (!rgb) return "#64748b";
  const yiq = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return yiq >= 145 ? "#334155" : "#ffffff";
}

// Quadrant-aware surface → slot mapping.
function getSlotMap(fdi) {
  const n = Number(fdi);
  const isLower     = (n >= 31 && n <= 48) || (n >= 71 && n <= 85);
  const isRightSide = (n >= 11 && n <= 18) || (n >= 41 && n <= 48)
                   || (n >= 51 && n <= 55) || (n >= 81 && n <= 85);
  return {
    B: isLower ? "bottom" : "top",
    L: isLower ? "top"    : "bottom",
    M: isRightSide ? "right" : "left",
    D: isRightSide ? "left"  : "right",
    O: "center",
  };
}

// =====================================================================
// Visual color resolution for a tooth, given the chart state.
// =====================================================================
//
// `entry` is one tooth's slice of the draft / snapshot state, shaped:
//   { findings: [
//       { finding_code: "caries", surfaces: ["M","O"] },
//       { finding_code: "note", notes: "..." },
//       ...
//     ]
//   }
//
// `colorByCode` is { caries: "#ef4444", restoration: "#3b82f6", ... }.
//
// Returns:
//   { surfaceColors:     { O: "#...", M: "#...", ... },
//     surfacePulp:       { O: boolean, M: boolean, ... },
//     singlePenMarkers:  [{ code, style }],
//     missing:           boolean,
//     hasNote:           boolean,
//     noteText:          string | null }
// =====================================================================
export function resolveToothVisual(entry, colorByCode, pulpByCode = {}) {
  const findings = entry?.findings ?? [];
  const surfaceColors = {};
  const surfacePulp = {};
  const singlePenMarkers = [];
  const wholeToothMarkers = [];
  let missing = false;
  let hasNote = false;
  let noteText = null;

  for (const f of findings) {
    if (isSinglePenCode(f.finding_code)) {
      const style = getSinglePenStyle(f.finding_code);
      singlePenMarkers.push({ code: f.finding_code, style });
      if (f.finding_code === "missing") missing = true;
      continue;
    }
    if (f.finding_code === "note") {
      const t = (f.notes || "").trim();
      if (t.length > 0) { hasNote = true; noteText = t; }
      continue;
    }
    const color = colorByCode[f.finding_code];
    if (!color) continue;
    const isPulp = !!pulpByCode[f.finding_code];
    const surfaces = f.surfaces ?? [];
    if (surfaces.length === 0) {
      if (isWholeToothSurfaceFillCode(f.finding_code)) {
        for (const s of ["M", "O", "D", "B", "L"]) {
          if (!surfaceColors[s]) {
            surfaceColors[s] = color;
            surfacePulp[s] = isPulp;
          } else if (isPulp) {
            surfacePulp[s] = true;
          }
        }
      }
      wholeToothMarkers.push({ code: f.finding_code, color, autoFlaggedBy: f.auto_flagged_by || null });
      continue;
    }
    for (const s of surfaces) {
      if (!surfaceColors[s]) {
        surfaceColors[s] = color;
        surfacePulp[s] = isPulp;
      } else if (isPulp) {
        surfacePulp[s] = true;
      }
    }
  }
  return { surfaceColors, surfacePulp, singlePenMarkers, wholeToothMarkers, missing, hasNote, noteText };
}

export default function Tooth({
  fdi,
  entry = null,                  // { findings: [...] } from state.teeth[fdi]
  traceEntry = null,             // optional snapshot entry to ghost behind
  colorByCode = {},              // { caries: "#ef4444", ... }
  pulpByCode = {},               // { caries_pulp: true, ... }
  active,                        // { kind: 'finding'|'erase', code? } or null
  readOnly = false,
  canEditNote,                   // independent gate for the note pencil; defaults to !readOnly
  planDots = null,               // [hex, ...] — overview "planned work" dots
  completedWork = [],            // Completed Work rows for this tooth, newest first
  dimFindings = false,           // chairside non-chart modes: dim the findings SVG so painted procedures stand out
  // Overlay layer — same shape as `entry`/`colorByCode` but painted
  // ON TOP of the base findings layer. Used by Doctor view in
  // Today / Tx Plan / Future Need modes so painted procedures
  // render as colored surfaces over (potentially dimmed) findings.
  overlayEntry = null,
  overlayColorByCode = null,
  overlayPulpByCode = null,
  stackSummary = null,             // DB-returned { stack_count, stacked_procedures, surface_columns, whole_tooth_columns }
  nameByCode = null,             // { code → human name } for hover-card summaries
  isTxMode = false,              // true on Tx Plan chart so hover labels read "On the plan" / "From the chart"
  onApplyFindingSurface,         // (fdi, surface) => void
  onEraseSurface,                // (fdi, surface) => void
  onClearTooth,                  // (fdi) => void  (eraser whole-tooth)
  onOpenNote,                    // (fdi) => void
  size = 60,
}) {
  const { surfaceColors, surfacePulp, singlePenMarkers, wholeToothMarkers, missing, hasNote, noteText } =
    resolveToothVisual(entry, colorByCode, pulpByCode);
  // Trace ghost — derived the same way; rendered at low opacity behind
  // the draft. When the trace has a note but the live entry doesn't,
  // we show the note pill as a ghost (translucent amber) to mirror the
  // way chart findings are ghosted under Tx Plan paint — so the
  // dentist can see at a glance which teeth have a chart-side note
  // even when the plan hasn't carried it (or has cleared it).
  const trace = traceEntry
    ? resolveToothVisual(traceEntry, colorByCode, pulpByCode)
    : null;
  // Overlay paint — wins over base surfaceColors when rendering.
  const overlay = overlayEntry && overlayColorByCode
    ? resolveToothVisual(overlayEntry, overlayColorByCode, overlayPulpByCode || {})
    : null;
  const overlaySurfaceColors = overlay?.surfaceColors ?? null;
  const baseSurfaceStackColumns = useMemo(
    () => buildVisualSurfaceStackColumns(entry, colorByCode),
    [entry, colorByCode],
  );
  const overlaySurfaceStackColumns = useMemo(
    () => overlayEntry && overlayColorByCode
      ? buildVisualSurfaceStackColumns(overlayEntry, overlayColorByCode)
      : {},
    [overlayEntry, overlayColorByCode],
  );
  const stackCount = Number(stackSummary?.stack_count || 0);
  const hasStack = stackCount > 1 && Array.isArray(stackSummary?.stacked_procedures) && stackSummary.stacked_procedures.length > 1;
  const wholeToothStackColumns = stackSummary?.whole_tooth_columns ?? [];
  const useWholeToothStack = hasStack && wholeToothStackColumns.length > 1;
  const visibleHasNote = hasNote || !!overlay?.hasNote;
  const visibleNoteText = noteText || overlay?.noteText || null;
  const singlePenTint = getSinglePenToothTint(singlePenMarkers);
  const singlePenOutline = getSinglePenToothOutline(singlePenMarkers);
  const cornerMarkers = getCornerMarkers(singlePenMarkers);
  const wholeToothFindingOutlines = wholeToothMarkers.slice(0, 3);
  const ghostNote = !hasNote && !!trace?.hasNote;
  const ghostNoteText = ghostNote ? trace?.noteText : null;
  const TRACE_OPACITY = 0.2;
  const FINDING_DIM_OPACITY = 0.28;
  const dimBaseOpacity = useCallback((opacity = 1) => (
    dimFindings ? Math.min(opacity, FINDING_DIM_OPACITY) : opacity
  ), [dimFindings]);

  const slotMap = getSlotMap(fdi);
  const lastPointerHandledRef = useRef({ surface: null, time: 0 });
  const POINTER_CLICK_SUPPRESS_MS = 80;
  const [hoverSurface, setHoverSurface] = useState(null);
  const [completedWorkOpen, setCompletedWorkOpen] = useState(false);
  const completedWorkOpenTimerRef = useRef(null);
  const completedWorkCloseTimerRef = useRef(null);

  const completedWorkItems = useMemo(() => normalizeCompletedWorkItems(completedWork), [completedWork]);
  const hasCompletedWork = completedWorkItems.length > 0;
  const stackBadgeCorner = getStackBadgeCorner(cornerMarkers, hasCompletedWork);
  const completedWorkSurfaces = useMemo(() => {
    const surfaces = new Set();
    for (const item of completedWorkItems) {
      for (const surface of item.surface_codes ?? []) surfaces.add(String(surface).toUpperCase());
    }
    return surfaces;
  }, [completedWorkItems]);
  const completedWorkWholeTooth = completedWorkItems.some((item) => (item.surface_codes ?? []).length === 0);

  const armed = !readOnly && active != null;
  const isFindingArmed = !readOnly && active?.kind === "finding";
  const isEraseArmed   = !readOnly && active?.kind === "erase";

  const clearCompletedWorkPreviewTimers = useCallback(() => {
    if (completedWorkOpenTimerRef.current) {
      clearTimeout(completedWorkOpenTimerRef.current);
      completedWorkOpenTimerRef.current = null;
    }
    if (completedWorkCloseTimerRef.current) {
      clearTimeout(completedWorkCloseTimerRef.current);
      completedWorkCloseTimerRef.current = null;
    }
  }, []);

  useEffect(() => clearCompletedWorkPreviewTimers, [clearCompletedWorkPreviewTimers]);

  const openCompletedWorkPreview = useCallback(() => {
    if (!hasCompletedWork) return;
    clearCompletedWorkPreviewTimers();
    setCompletedWorkOpen(true);
  }, [clearCompletedWorkPreviewTimers, hasCompletedWork]);

  const scheduleOpenCompletedWorkPreview = useCallback(() => {
    if (!hasCompletedWork || completedWorkOpen) return;
    if (completedWorkCloseTimerRef.current) {
      clearTimeout(completedWorkCloseTimerRef.current);
      completedWorkCloseTimerRef.current = null;
    }
    if (completedWorkOpenTimerRef.current) clearTimeout(completedWorkOpenTimerRef.current);
    completedWorkOpenTimerRef.current = setTimeout(() => {
      completedWorkOpenTimerRef.current = null;
      setCompletedWorkOpen(true);
    }, COMPLETED_WORK_PREVIEW_OPEN_DELAY_MS);
  }, [completedWorkOpen, hasCompletedWork]);

  const scheduleCloseCompletedWorkPreview = useCallback(() => {
    if (completedWorkOpenTimerRef.current) {
      clearTimeout(completedWorkOpenTimerRef.current);
      completedWorkOpenTimerRef.current = null;
    }
    if (completedWorkCloseTimerRef.current) clearTimeout(completedWorkCloseTimerRef.current);
    completedWorkCloseTimerRef.current = setTimeout(() => setCompletedWorkOpen(false), COMPLETED_WORK_PREVIEW_CLOSE_DELAY_MS);
  }, []);

  const handleSurfaceActivation = useCallback((surface, e) => {
    e?.stopPropagation?.();
    if (isEraseArmed) {
      // "Missing" is a whole-tooth finding — surface erase preserves it
      // (chartState.eraseSurface skips findings without surfaces). The
      // intent when a user clicks the eraser on a missing tooth is to
      // un-mark missing, so route through clearTooth instead.
      if (missing) { onClearTooth?.(fdi); return; }
      onEraseSurface?.(fdi, surface);
      return;
    }
    if (isFindingArmed) {
      onApplyFindingSurface?.(fdi, surface);
      return;
    }
  }, [isEraseArmed, isFindingArmed, missing, fdi, onEraseSurface, onClearTooth, onApplyFindingSurface]);

  const handleSurfacePointerUp = useCallback((surface, e) => {
    e.stopPropagation();
    lastPointerHandledRef.current = { surface, time: Date.now() };
    handleSurfaceActivation(surface, e);
  }, [handleSurfaceActivation]);

  const handleSurfaceClick = useCallback((surface, e) => {
    const recentPointer = lastPointerHandledRef.current;
    if (
      recentPointer.surface === surface &&
      Date.now() - recentPointer.time < POINTER_CLICK_SUPPRESS_MS
    ) {
      e.stopPropagation();
      return;
    }
    handleSurfaceActivation(surface, e);
  }, [handleSurfaceActivation]);

  // Surface render helper (a rounded "pill" or the centre circle).
  const Surface = ({ surface }) => {
    const slot = slotMap[surface];
    const dbSurfaceStackColumns = stackSummary?.surface_columns?.[surface] ?? [];
    const derivedSurfaceStackColumns =
      overlaySurfaceStackColumns?.[surface]?.length > 1
        ? overlaySurfaceStackColumns[surface]
        : baseSurfaceStackColumns?.[surface]?.length > 1
          ? baseSurfaceStackColumns[surface]
          : [];
    const surfaceStackColumns = dbSurfaceStackColumns.length > 0
      ? dbSurfaceStackColumns
      : derivedSurfaceStackColumns;
    const useSurfaceStack = surfaceStackColumns.length > 1;
    const singleSurfaceStackFill = dbSurfaceStackColumns.length === 1 ? dbSurfaceStackColumns[0]?.color : null;
    // Overlay (painted procedures) wins over the base findings layer
    // for the same surface — so a Composite painted on tooth #14's M
    // surface visually overrides any caries finding on the same spot.
    const baseFill = surfaceColors[surface];
    const overlayFill = overlaySurfaceColors?.[surface];
    const completedWorkFill = completedWorkWholeTooth || completedWorkSurfaces.has(surface)
      ? COMPLETED_WORK_COLOR
      : null;
    const tintFill = !overlayFill && !completedWorkFill && !baseFill ? singlePenTint?.fillColor : null;
    const fill = singleSurfaceStackFill || (!useWholeToothStack ? overlayFill : null) || completedWorkFill || baseFill || tintFill;
    const isPulpSurface = !!(overlayFill ? overlay?.surfacePulp?.[surface] : surfacePulp?.[surface]);
    const traceFill = trace?.surfaceColors?.[surface];
    const filled = !!fill;
    const isCenter = slot === "center";
    // Dim ONLY the base findings layer when dimFindings is on. The
    // overlay paint stays at full opacity so painted procedures pop
    // against the (faint) existing chart.
    const fillOpacityProp = tintFill
      ? dimBaseOpacity(singlePenTint.opacity)
      : completedWorkFill && !overlayFill
        ? 0.72
      : !overlayFill && dimFindings ? FINDING_DIM_OPACITY : undefined;

    const interactive = armed;
    const cls = `transition-colors ${
      filled ? "" : interactive ? "hover:fill-slate-100" : ""
    } ${isEraseArmed && filled ? "hover:opacity-50" : ""}`;

    const pos = LABEL_POS[slot];
    const labelColor = (useSurfaceStack || useWholeToothStack)
      ? "#ffffff"
      : filled
        ? readableSurfaceLabelColor(fill)
      : traceFill
        ? "#64748b"
        : "#94a3b8";
    const labelSize  = surface === "O" ? 5.5 : 5;
    const labelStroke = filled && labelColor !== "#ffffff" ? "rgba(255,255,255,0.7)" : "rgba(15,23,42,0.24)";

    // Three exclusive visual states per surface. The M/O/D/B/L label is
    // rendered after the fill so surface names stay visible in every state.
    //   draft fill (filled)         → opaque color, no stroke
    //   trace ghost (traceFill)     → 20% opacity color, no stroke
    //   empty                       → white with thin stroke
    let fillNode;
    if (useSurfaceStack) {
      fillNode = (
        <StackedSurfaceFill
          fdi={fdi}
          surface={surface}
          slot={slot}
          columns={surfaceStackColumns}
          isPulpSurface={isPulpSurface}
          className={cls}
        />
      );
    } else if (useWholeToothStack) {
      fillNode = isCenter
        ? <circle cx={CX} cy={CY} r={CENTER_R} fill="transparent" stroke="rgba(255,255,255,0.42)" strokeWidth={0.5} className={cls} />
        : <path d={PILL_PATHS[slot]} fill="transparent" stroke="rgba(255,255,255,0.42)" strokeWidth={0.5} className={cls} />;
    } else if (isCenter) {
      if (filled) {
        fillNode = <circle cx={CX} cy={CY} r={CENTER_R} fill={fill} fillOpacity={fillOpacityProp} stroke={isPulpSurface ? PULP_OUTLINE_COLOR : "none"} strokeWidth={isPulpSurface ? 1.5 : 0} className={cls} />;
      } else if (traceFill) {
        fillNode = <circle cx={CX} cy={CY} r={CENTER_R} fill={traceFill} fillOpacity={TRACE_OPACITY} stroke="none" pointerEvents="none" />;
      } else {
        fillNode = <circle cx={CX} cy={CY} r={CENTER_R} fill="#ffffff" stroke="#e2e8f0" strokeWidth={0.5} className={cls} />;
      }
    } else {
      if (filled) {
        fillNode = <path d={PILL_PATHS[slot]} fill={fill} fillOpacity={fillOpacityProp} stroke={isPulpSurface ? PULP_OUTLINE_COLOR : "none"} strokeWidth={isPulpSurface ? 1.5 : 0} className={cls} />;
      } else if (traceFill) {
        fillNode = <path d={PILL_PATHS[slot]} fill={traceFill} fillOpacity={TRACE_OPACITY} stroke="none" pointerEvents="none" />;
      } else {
        fillNode = <path d={PILL_PATHS[slot]} fill="#ffffff" stroke="#e2e8f0" strokeWidth={0.5} className={cls} />;
      }
    }

    // Hit-expand zone for interactive picks (transparent).
    const hitNode = interactive ? (
      isCenter
        ? <circle cx={CX} cy={CY} r={8.5} fill="transparent" />
        : <path d={PILL_PATHS[slot]} fill="transparent" stroke="transparent" strokeWidth={1.5} />
    ) : null;

    return (
      <g
        data-testid={`tooth-${fdi}-surface-${surface}`}
        data-tutorial-id={`charting-tooth-${fdi}-surface-${String(surface).toLowerCase()}`}
        data-tutorial-spotlight-shape={isCenter ? "circle" : "pill"}
        data-completed-work={completedWorkFill ? "true" : "false"}
        onPointerUp={(e) => handleSurfacePointerUp(surface, e)}
        onClick={(e) => handleSurfaceClick(surface, e)}
        onMouseEnter={() => setHoverSurface(surface)}
        onMouseLeave={() => setHoverSurface(null)}
        style={{ cursor: interactive ? (isEraseArmed ? "pointer" : "crosshair") : "default" }}
      >
        {hitNode}
        {fillNode}
        <text
          x={pos.x} y={pos.y}
          textAnchor="middle" dominantBaseline="central"
          pointerEvents="none"
          fontSize={labelSize} fontWeight="700" fill={labelColor}
          stroke={labelStroke}
          strokeWidth={0.45}
          paintOrder="stroke fill"
        >
          {surface}
        </text>
      </g>
    );
  };

  // Tooth size is read from the parent's --tooth-size CSS variable so the
  // chart can shrink/grow fluidly across breakpoints. The numeric `size`
  // prop is only the fallback when no variable is set (and a hint for
  // the wrapper width).
  const sizeVar = `var(--tooth-size, ${size}px)`;
  const wrapperWidthVar = `calc(${sizeVar} + 4px)`;

  return (
    <div
      className="relative flex flex-col items-center gap-1 select-none"
      data-testid={`tooth-${fdi}`}
      onMouseEnter={scheduleOpenCompletedWorkPreview}
      onMouseLeave={scheduleCloseCompletedWorkPreview}
      style={{ width: wrapperWidthVar }}
    >
      <div className="flex items-center gap-1">
        <span className="text-[11px] font-semibold text-slate-600 tabular-nums">{fdi}</span>
        {planDots && planDots.length > 0 ? (
          <div
            className="flex items-center gap-0.5"
            data-testid={`tooth-${fdi}-plan-dots`}
            title={`${planDots.length} planned procedure${planDots.length > 1 ? "s" : ""}`}
          >
            {planDots.slice(0, 3).map((hex, i) => (
              <span
                key={i}
                className="inline-block w-1.5 h-1.5 rounded-full ring-1 ring-white"
                style={{ backgroundColor: hex }}
                aria-hidden
              />
            ))}
            {planDots.length > 3 ? (
              <span className="text-[8px] font-semibold text-slate-500 leading-none">
                +{planDots.length - 3}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="relative" style={{ width: sizeVar, height: sizeVar }}>
        <svg
          viewBox={`0 0 ${VIEW} ${VIEW}`}
          className="block"
          role="img"
          data-tutorial-id={`charting-tooth-${fdi}`}
          data-tutorial-spotlight-shape="circle"
          aria-label={`Tooth ${fdi}${missing ? " (missing)" : ""}`}
          onClick={(e) => {
            if (!isEraseArmed) return;
            // Whole-tooth eraser: click empty area clears all surfaces of this tooth.
            if (e.target.tagName === "svg") onClearTooth?.(fdi);
          }}
        style={{
          width: sizeVar,
          height: sizeVar,
          touchAction: "manipulation",
          cursor: armed ? (isEraseArmed ? "pointer" : "crosshair") : "default",
        }}
        >
          {useWholeToothStack ? (
            <StackedToothFill fdi={fdi} columns={wholeToothStackColumns} />
          ) : null}
          <Surface surface="B" />
          <Surface surface="M" />
          <Surface surface="D" />
          <Surface surface="L" />
          <Surface surface="O" />

        {singlePenOutline ? (
          <circle
            cx={CX}
            cy={CY}
            r={18.4}
            fill="none"
            stroke={singlePenOutline.borderColor}
            strokeWidth={1.2}
            strokeOpacity={dimFindings ? FINDING_DIM_OPACITY : 1}
            strokeDasharray={singlePenOutline.toothOutline === "dashed" ? "2 2" : undefined}
            pointerEvents="none"
          />
        ) : null}

        {wholeToothFindingOutlines.map((marker, index) => (
          <circle
            key={`${marker.code}-${index}`}
            cx={CX}
            cy={CY}
            r={16.1 + (index * 1.8)}
            fill="none"
            stroke={marker.color}
            strokeWidth={1.1}
            strokeOpacity={dimFindings ? FINDING_DIM_OPACITY : (marker.autoFlaggedBy ? 0.65 : 0.9)}
            strokeDasharray={marker.autoFlaggedBy ? "2 2" : undefined}
            pointerEvents="none"
          />
        ))}

        {cornerMarkers.map((marker) => (
          <CornerMarker
            key={`${marker.code}-${marker.cornerPosition}`}
            marker={marker}
            opacity={dimFindings ? FINDING_DIM_OPACITY : 1}
          />
        ))}

        {singlePenMarkers.some((marker) => marker.style?.overlaySymbol === "lightningRing") ? (
          <SensitivityRing opacity={dimFindings ? FINDING_DIM_OPACITY : 1} />
        ) : null}

        {missing ? (
          <>
            <line x1={6} y1={6} x2={34} y2={34}
                  stroke="#4B5563" strokeWidth={1.7}
                  strokeOpacity={dimFindings ? FINDING_DIM_OPACITY : 1}
                  strokeLinecap="round" pointerEvents="none" />
            <line x1={34} y1={6} x2={6} y2={34}
                  stroke="#4B5563" strokeWidth={1.7}
                  strokeOpacity={dimFindings ? FINDING_DIM_OPACITY : 1}
                  strokeLinecap="round" pointerEvents="none" />
          </>
        ) : trace?.missing ? (
          <>
            <line x1={6} y1={6} x2={34} y2={34}
                  stroke="#94a3b8" strokeWidth={1.2}
                  strokeOpacity={TRACE_OPACITY}
                  strokeLinecap="round" pointerEvents="none" />
            <line x1={34} y1={6} x2={6} y2={34}
                  stroke="#94a3b8" strokeWidth={1.2}
                  strokeOpacity={TRACE_OPACITY}
                  strokeLinecap="round" pointerEvents="none" />
          </>
        ) : null}

        {hasCompletedWork ? (
          <CompletedWorkCheck fdi={fdi} />
        ) : null}
        </svg>
        {hasStack ? (
          <StackBadgePopover
            fdi={fdi}
            summary={stackSummary}
            corner={stackBadgeCorner}
          />
        ) : null}
      </div>

      {hasCompletedWork && completedWorkOpen ? (
        <CompletedWorkPopover
          fdi={fdi}
          items={completedWorkItems}
          onClose={() => {
            clearCompletedWorkPreviewTimers();
            setCompletedWorkOpen(false);
          }}
          onMouseEnter={openCompletedWorkPreview}
          onMouseLeave={scheduleCloseCompletedWorkPreview}
        />
      ) : null}

      <NotePill
        fdi={fdi}
        hasNote={visibleHasNote}
        noteText={visibleNoteText}
        ghostNote={ghostNote}
        ghostNoteText={ghostNoteText}
        readOnly={canEditNote == null ? readOnly : !canEditNote}
        onClick={() => onOpenNote?.(fdi)}
        entry={entry}
        traceEntry={traceEntry}
        nameByCode={nameByCode}
        isTxMode={isTxMode}
      />
    </div>
  );
}

function getSinglePenToothTint(markers = []) {
  const priority = ["missing", "to_extract", "discoloration"];
  const marker = priority
    .map((code) => markers.find((item) => item.code === code))
    .find(Boolean);
  if (!marker?.style) return null;
  const opacityByCode = {
    to_extract: 0.25,
    discoloration: 0.4,
  };
  return {
    fillColor: marker.style.fillColor,
    opacity: opacityByCode[marker.code] ?? 1,
  };
}

function buildVisualSurfaceStackColumns(entry, colorByCode = {}) {
  const surfaceColumns = {};
  for (const finding of entry?.findings ?? []) {
    if (isSinglePenCode(finding.finding_code) || finding.finding_code === "note") continue;
    const color = colorByCode[finding.finding_code];
    if (!color) continue;
    const rawSurfaces = Array.isArray(finding.surfaces) ? finding.surfaces : [];
    const surfaces = rawSurfaces.length === 0 && isWholeToothSurfaceFillCode(finding.finding_code)
      ? ["M", "O", "D", "B", "L"]
      : rawSurfaces;
    for (const surface of surfaces) {
      const code = String(surface || "").toUpperCase();
      if (!["M", "O", "D", "B", "L"].includes(code)) continue;
      if (!surfaceColumns[code]) surfaceColumns[code] = [];
      surfaceColumns[code].push({
        procedure_name: finding.finding_code,
        color,
      });
    }
  }
  return Object.fromEntries(
    Object.entries(surfaceColumns).map(([surface, columns]) => [surface, columns.slice(0, 4)]),
  );
}

function StackedToothFill({ fdi, columns = [] }) {
  if (!Array.isArray(columns) || columns.length <= 1) return null;
  const clipId = `tooth-${fdi}-stack-whole`;
  const width = VIEW / columns.length;
  return (
    <g pointerEvents="none" aria-hidden data-testid={`tooth-${fdi}-stack-whole-fill`}>
      <defs>
        <clipPath id={clipId}>
          <circle cx={CX} cy={CY} r={OUTER_R} />
        </clipPath>
      </defs>
      {columns.map((item, index) => (
        <rect
          key={`${item.procedure_name || "procedure"}-${index}`}
          x={index * width}
          y={0}
          width={index === columns.length - 1 ? VIEW - index * width : width}
          height={VIEW}
          fill={item.color || STACK_FALLBACK_COLOR}
          clipPath={`url(#${clipId})`}
          data-testid={`tooth-${fdi}-stack-whole-column-${index}`}
        />
      ))}
      <circle cx={CX} cy={CY} r={OUTER_R} fill="none" stroke="#ffffff" strokeWidth={0.8} opacity={0.72} />
    </g>
  );
}

function StackedSurfaceFill({ fdi, surface, slot, columns = [], isPulpSurface, className }) {
  if (!Array.isArray(columns) || columns.length <= 1) return null;
  const clipId = `tooth-${fdi}-${surface}-stack-surface`;
  const bounds = SURFACE_STACK_BOUNDS[slot] || { x: 0, y: 0, width: VIEW, height: VIEW };
  const width = bounds.width / columns.length;
  const shape = slot === "center"
    ? <circle cx={CX} cy={CY} r={CENTER_R} />
    : <path d={PILL_PATHS[slot]} />;
  const strokeShape = slot === "center"
    ? <circle cx={CX} cy={CY} r={CENTER_R} fill="none" stroke={isPulpSurface ? PULP_OUTLINE_COLOR : "rgba(255,255,255,0.58)"} strokeWidth={isPulpSurface ? 1.5 : 0.5} className={className} />
    : <path d={PILL_PATHS[slot]} fill="none" stroke={isPulpSurface ? PULP_OUTLINE_COLOR : "rgba(255,255,255,0.58)"} strokeWidth={isPulpSurface ? 1.5 : 0.5} className={className} />;

  return (
    <g data-testid={`tooth-${fdi}-surface-${surface}-stack-fill`}>
      <defs>
        <clipPath id={clipId}>{shape}</clipPath>
      </defs>
      {columns.map((item, index) => (
        <rect
          key={`${item.procedure_name || "procedure"}-${index}`}
          x={bounds.x + index * width}
          y={bounds.y}
          width={index === columns.length - 1 ? bounds.width - index * width : width}
          height={bounds.height}
          fill={item.color || STACK_FALLBACK_COLOR}
          clipPath={`url(#${clipId})`}
          className={className}
          data-testid={`tooth-${fdi}-surface-${surface}-stack-column-${index}`}
        />
      ))}
      {strokeShape}
    </g>
  );
}

function getStackBadgeCorner(markers = [], hasCompletedWork = false) {
  const occupied = new Set((markers || []).map((marker) => marker.cornerPosition).filter(Boolean));
  if (hasCompletedWork) occupied.add("topRight");
  return ["topLeft", "bottomLeft", "bottomRight", "topRight"].find((corner) => !occupied.has(corner)) || "bottomLeft";
}

const STACK_BADGE_CORNER_CLASS = {
  topLeft: "left-0 top-0 -translate-x-1 -translate-y-1",
  bottomLeft: "left-0 bottom-0 -translate-x-1 translate-y-1",
  bottomRight: "right-0 bottom-0 translate-x-1 translate-y-1",
  topRight: "right-0 top-0 translate-x-1 -translate-y-1",
};

function stackStatusClass(status) {
  const s = String(status || "").toLowerCase();
  if (["accepted", "active", "done", "completed", "complete"].includes(s)) return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (["proposed", "draft", "candidate", "pending"].includes(s)) return "border-amber-200 bg-amber-50 text-amber-700";
  if (["declined", "cancelled", "canceled"].includes(s)) return "border-rose-200 bg-rose-50 text-rose-700";
  return "border-slate-200 bg-slate-50 text-slate-600";
}

function StackBadgePopover({ fdi, summary, corner }) {
  const [open, setOpen] = useState(false);
  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);
  const procedures = Array.isArray(summary?.stacked_procedures) ? summary.stacked_procedures : [];
  const count = Number(summary?.stack_count || procedures.length);

  const clearTimers = useCallback(() => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const openNow = useCallback(() => {
    clearTimers();
    setOpen(true);
  }, [clearTimers]);

  const scheduleOpen = useCallback(() => {
    if (open) return;
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    openTimerRef.current = setTimeout(() => {
      openTimerRef.current = null;
      setOpen(true);
    }, STACK_PREVIEW_OPEN_DELAY_MS);
  }, [open]);

  const scheduleClose = useCallback(() => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setOpen(false), STACK_PREVIEW_CLOSE_DELAY_MS);
  }, []);

  if (count <= 1 || procedures.length <= 1) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`absolute z-20 ${STACK_BADGE_CORNER_CLASS[corner] || STACK_BADGE_CORNER_CLASS.topLeft}`}
          aria-label={`Tooth ${fdi} has ${count} stacked procedures`}
          data-testid={`tooth-${fdi}-stack-badge`}
          onClick={(e) => {
            e.stopPropagation();
            openNow();
          }}
          onMouseEnter={scheduleOpen}
          onMouseLeave={scheduleClose}
          onFocus={openNow}
          onBlur={scheduleClose}
          style={{ touchAction: "manipulation" }}
        >
          <Badge className="flex h-4 min-w-4 items-center justify-center rounded-full border border-white bg-slate-900 px-1 text-[9px] font-bold leading-none text-white shadow-sm hover:bg-slate-800">
            {count}
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align={corner?.includes("Right") ? "end" : "start"}
        sideOffset={6}
        avoidCollisions
        collisionPadding={12}
        className="w-80 p-3 text-xs"
        data-testid={`tooth-${fdi}-stack-popover`}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onMouseEnter={openNow}
        onMouseLeave={scheduleClose}
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Stacked Procedures</p>
            <p className="font-semibold text-slate-900">Tooth #{fdi}</p>
          </div>
          <Badge variant="outline" className="rounded-full px-2 py-0 text-[10px] text-slate-600">
            {count}
          </Badge>
        </div>
        <ul className="space-y-1.5">
          {procedures.map((proc, index) => (
            <li
              key={`${proc.procedure_name || "procedure"}-${index}`}
              className="grid grid-cols-[minmax(0,1fr)_auto_minmax(4.5rem,auto)] items-center gap-2 rounded-md border border-slate-100 bg-slate-50/70 px-2 py-1.5"
            >
              <div className="flex min-w-0 items-center gap-1.5">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-black/10"
                  style={{ backgroundColor: proc.color || "#94a3b8" }}
                  aria-hidden
                />
                <span className="truncate font-medium text-slate-800">{proc.procedure_name || "Procedure"}</span>
              </div>
              <Badge variant="outline" className={`rounded-full px-1.5 py-0 text-[10px] ${stackStatusClass(proc.status)}`}>
                {proc.status || "No status"}
              </Badge>
              <span className="truncate text-right text-[11px] text-slate-500">{proc.assigned_doctor || "Unassigned"}</span>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

function getSinglePenToothOutline(markers = []) {
  const marker = markers.find((item) => item.style?.toothOutline && item.style.toothOutline !== "none");
  return marker?.style || null;
}

function getCornerMarkers(markers = []) {
  const out = [];
  const rct = markers.find((marker) => marker.code === "rct_needed");
  if (rct?.style) {
    out.push({ ...rct, cornerPosition: "topRight" });
  }
  const watch = markers.find((marker) => marker.code === "watch");
  if (watch?.style) {
    out.push({
      ...watch,
      cornerPosition: getPillPosition(out),
    });
  }
  return out;
}

const CORNER_BOX = {
  topRight: { x: 25, y: 1, anchor: "middle" },
  topLeft: { x: 1, y: 1, anchor: "middle" },
  bottomRight: { x: 22, y: 31, anchor: "middle" },
  bottomLeft: { x: 1, y: 31, anchor: "middle" },
};

function CornerMarker({ marker, opacity = 1 }) {
  const box = CORNER_BOX[marker.cornerPosition] || CORNER_BOX.topLeft;
  if (marker.code === "rct_needed") {
    return (
      <g pointerEvents="none" opacity={opacity}>
        <rect
          x={box.x}
          y={box.y}
          width={14}
          height={8}
          rx={2.5}
          fill={marker.style.fillColor}
          stroke={marker.style.borderColor}
          strokeWidth={0.8}
        />
        <text
          x={box.x + 7}
          y={box.y + 4.35}
          textAnchor={box.anchor}
          dominantBaseline="central"
          fontSize={3.8}
          fontWeight="700"
          fill="#7C2D12"
        >
          RCT
        </text>
      </g>
    );
  }
  if (marker.code === "watch") {
    return (
      <g pointerEvents="none" opacity={opacity}>
        <rect
          x={box.x}
          y={box.y}
          width={17}
          height={7}
          rx={3.5}
          fill={marker.style.fillColor}
          stroke={marker.style.borderColor}
          strokeWidth={0.7}
        />
        <text
          x={box.x + 8.5}
          y={box.y + 3.8}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={3.9}
          fontWeight="600"
          fill={marker.style.textColor}
        >
          Watch
        </text>
      </g>
    );
  }
  return null;
}

function SensitivityRing({ opacity = 1 }) {
  return (
    <g pointerEvents="none" aria-hidden opacity={opacity}>
      <circle
        cx={CX}
        cy={CY}
        r={19}
        fill="none"
        stroke="#D97706"
        strokeWidth={0.8}
        strokeOpacity={0.32}
      />
      <path
        d="M29.2 24.8 26.8 30h3.4l-3.3 6.4"
        fill="none"
        stroke="#D97706"
        strokeWidth={0.9}
        strokeOpacity={0.42}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
}

function CompletedWorkCheck({ fdi }) {
  return (
    <g data-testid={`tooth-${fdi}-completed-work-check`} pointerEvents="none" aria-hidden>
      <circle
        cx={32.2}
        cy={7.8}
        r={5.4}
        fill="#0f766e"
        stroke="#ffffff"
        strokeWidth={1.1}
      />
      <path
        d="M29.8 7.8 31.4 9.4 34.8 5.8"
        fill="none"
        stroke="#ffffff"
        strokeWidth={1.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
}

function normalizeCompletedWorkItems(items = []) {
  return [...items]
    .filter(Boolean)
    .map((item) => ({
      ...item,
      surface_codes: Array.isArray(item.surface_codes)
        ? item.surface_codes.map((surface) => String(surface).toUpperCase()).filter(Boolean)
        : [],
      completed_at: item.completed_at ?? item.occurred_at ?? null,
      performed_by_name: item.performed_by_name ?? item.doctor_name ?? null,
    }))
    .sort((a, b) => {
      const aTime = a.completed_at ? new Date(a.completed_at).getTime() : 0;
      const bTime = b.completed_at ? new Date(b.completed_at).getTime() : 0;
      return bTime - aTime;
    });
}

function formatCompletedWorkDate(value) {
  if (!value) return "Date unknown";
  try {
    return new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return value;
  }
}

function completedWorkTargetLabel(item) {
  const surfaces = Array.isArray(item.surface_codes) ? item.surface_codes.filter(Boolean) : [];
  return surfaces.length > 0 ? surfaces.join("") : "Whole tooth";
}

function CompletedWorkPopover({ fdi, items, onClose, onMouseEnter, onMouseLeave }) {
  return (
    <div
      role="tooltip"
      data-testid={`tooth-${fdi}-completed-work-popover`}
      className="absolute bottom-full left-1/2 z-50 mb-4 w-72 -translate-x-1/2 rounded-xl border border-emerald-200 bg-white p-3 text-xs shadow-lg"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Completed Work</p>
          <p className="font-semibold text-slate-900">Tooth #{fdi}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close completed work"
          className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
        >
          <XIcon className="h-3.5 w-3.5" />
        </button>
      </div>
      <ul className="space-y-2">
        {items.map((item, index) => (
          <li key={item.id ?? `${item.procedure_name}-${index}`} className="rounded-lg bg-emerald-50/70 px-2 py-1.5">
            <p className="font-semibold text-slate-900">{item.procedure_name || "Procedure"}</p>
            <p className="text-slate-600">
              {completedWorkTargetLabel(item)}
              {item.completed_at ? ` · ${formatCompletedWorkDate(item.completed_at)}` : ""}
            </p>
            {item.performed_by_name ? (
              <p className="text-slate-500">{item.performed_by_name}</p>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Render a one-line summary string for a tooth entry: skip the note
// finding (rendered separately above the summary), look up each
// finding's human-readable name in nameByCode, and append surfaces in
// parens when present. e.g. "Caries (M, O), Old composite (D)".
function summarizeEntry(entry, nameByCode) {
  const findings = entry?.findings ?? [];
  const parts = [];
  for (const f of findings) {
    if (f.finding_code === "note") continue;
    const name = nameByCode?.[f.finding_code] || f.finding_code;
    const surfaces = Array.isArray(f.surfaces) && f.surfaces.length > 0
      ? ` (${f.surfaces.join(", ")})` : "";
    parts.push(`${name}${surfaces}`);
  }
  return parts.join(", ");
}

function NotePill({ fdi, hasNote, noteText, ghostNote, ghostNoteText, readOnly, onClick, entry, traceEntry, nameByCode, isTxMode }) {
  const filled = hasNote;
  const ghosted = !filled && ghostNote;
  const [open, setOpen] = useState(false);
  const hoverOpenTimerRef = useRef(null);
  const hoverCloseTimerRef = useRef(null);

  const title = filled
    ? (readOnly ? noteText || "Note" : "Edit note")
    : ghosted
      ? (readOnly ? ghostNoteText || "Note (from chart)" : "Add note (chart has one)")
      : (readOnly ? "No note" : "Add note");

  const liveSummary  = summarizeEntry(entry, nameByCode);
  const traceSummary = summarizeEntry(traceEntry, nameByCode);
  const liveLabel    = isTxMode ? "On the plan" : "Findings";
  const traceLabel   = isTxMode ? "From the chart" : "Previous chart";
  const effectiveNote = noteText || ghostNoteText;
  const noteSnippet  = effectiveNote ? (effectiveNote.length > 140 ? `${effectiveNote.slice(0, 140)}…` : effectiveNote) : null;
  const isEmpty      = !noteSnippet && !liveSummary && !traceSummary;
  const editLabel    = filled ? "Edit note" : ghosted ? "Bring chart note in" : "Add note";

  // Pill chrome:
  //   filled (live note)   → solid amber, white pencil
  //   ghosted (chart note) → translucent amber so it reads as ghost
  //   empty                → outlined, gray pencil
  const pillClass = filled
    ? "bg-amber-500 hover:bg-amber-600"
    : ghosted
      ? "bg-amber-500/30 hover:bg-amber-500/50 border border-amber-300/70"
      : "border border-slate-300 bg-white hover:border-slate-400 disabled:opacity-50";
  const pencilClass = filled
    ? "text-white"
    : ghosted
      ? "text-amber-700/70"
      : "text-slate-400";

  // Tap-then-edit gesture (works identically on mouse and touch):
  //   1st click on pill  → popover opens with summary
  //   2nd click ("Edit") → popover closes, modal opens
  // No HoverCard, no input-method detection.
  const clearPreviewTimers = () => {
    if (hoverOpenTimerRef.current) {
      clearTimeout(hoverOpenTimerRef.current);
      hoverOpenTimerRef.current = null;
    }
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
  };

  useEffect(() => clearPreviewTimers, []);

  const handleEdit = () => {
    clearPreviewTimers();
    setOpen(false);
    onClick?.();
  };

  const openPreview = () => {
    clearPreviewTimers();
    setOpen(true);
  };

  const scheduleOpenPreview = () => {
    if (open) return;
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
    if (hoverOpenTimerRef.current) clearTimeout(hoverOpenTimerRef.current);
    hoverOpenTimerRef.current = setTimeout(() => {
      hoverOpenTimerRef.current = null;
      setOpen(true);
    }, NOTE_PREVIEW_OPEN_DELAY_MS);
  };

  const scheduleClosePreview = () => {
    if (hoverOpenTimerRef.current) {
      clearTimeout(hoverOpenTimerRef.current);
      hoverOpenTimerRef.current = null;
    }
    if (hoverCloseTimerRef.current) clearTimeout(hoverCloseTimerRef.current);
    hoverCloseTimerRef.current = setTimeout(() => setOpen(false), NOTE_PREVIEW_CLOSE_DELAY_MS);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openPreview();
          }}
          onMouseEnter={scheduleOpenPreview}
          onMouseLeave={scheduleClosePreview}
          onFocus={openPreview}
          onBlur={scheduleClosePreview}
          disabled={readOnly && !filled && !ghosted}
          aria-label={title}
          data-testid={`tooth-${fdi}-note-pill`}
          data-note-state={filled ? "live" : ghosted ? "ghost" : "empty"}
          className={`h-8 w-full max-w-10 rounded-full flex items-center justify-center transition-colors min-[900px]:h-5 ${pillClass}`}
          style={{ touchAction: "manipulation" }}
        >
          <Pencil className={`w-3 h-3 ${pencilClass}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="center"
        className="w-72 p-3 text-xs space-y-2"
        data-testid={`tooth-${fdi}-popover`}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onMouseEnter={openPreview}
        onMouseLeave={scheduleClosePreview}
      >
        <div className="flex items-center justify-between">
          <div className="font-semibold text-slate-800 text-sm">Tooth #{fdi}</div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-600"
          >
            <XIcon className="w-3.5 h-3.5" />
          </button>
        </div>

        {isEmpty ? (
          <p className="text-slate-400 italic">No notes or findings yet.</p>
        ) : (
          <>
            {noteSnippet ? (
              <div className="flex items-start gap-1.5">
                <MessageSquare className="w-3 h-3 mt-0.5 shrink-0 text-amber-500" />
                <p className="italic text-slate-700 whitespace-pre-wrap">{noteSnippet}</p>
              </div>
            ) : null}
            {liveSummary ? (
              <div>
                <div className="text-[10px] font-bold tracking-wider uppercase text-slate-400">{liveLabel}</div>
                <p className="text-slate-700">{liveSummary}</p>
              </div>
            ) : null}
            {traceSummary ? (
              <div>
                <div className="text-[10px] font-bold tracking-wider uppercase text-slate-400">{traceLabel}</div>
                <p className="text-slate-700">{traceSummary}</p>
              </div>
            ) : null}
          </>
        )}

        {readOnly ? null : (
          <button
            type="button"
            onClick={handleEdit}
            data-testid={`tooth-${fdi}-popover-edit`}
            className="w-full mt-2 px-3 py-1.5 text-xs font-semibold bg-amber-500 text-white rounded-md hover:bg-amber-600 inline-flex items-center justify-center gap-1.5"
          >
            <Pencil className="w-3 h-3" /> {editLabel}
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
